import { ReactElement, ReactNode, useState } from 'react';
import clsx from 'clsx';
import { useAuth } from '@common/auth/use-auth';
import { NotificationDialogTrigger } from '@common/notifications/dialog/notification-dialog-trigger';
import { Menu, MenuTrigger } from '@ui/menu/menu-trigger';
import { useCustomMenu } from '@common/menus/use-custom-menu';
import { createSvgIconFromTree } from '@ui/icons/create-svg-icon';
import { Trans } from '@ui/i18n/trans';
import { IconButton } from '@ui/buttons/icon-button';
import { Item } from '@ui/forms/listbox/item';
import { useNavigate } from '@common/ui/navigation/use-navigate';
import { useIsDarkMode } from '@ui/themes/use-is-dark-mode';
import { CustomMenu } from '@common/menus/custom-menu';
import { useSettings } from '@ui/settings/use-settings';
import { ButtonColor } from '@ui/buttons/get-shared-button-style';
import { MenuIcon } from '@ui/icons/material/Menu';
import {
  NavbarAuthUser,
  NavbarAuthUserProps,
} from '@common/ui/navigation/navbar/navbar-auth-user';
import { NavbarAuthButtons } from '@common/ui/navigation/navbar/navbar-auth-buttons';
import { useDarkThemeVariables } from '@ui/themes/use-dark-theme-variables';
import { Logo } from '@common/ui/navigation/navbar/logo';
import { useLightThemeVariables } from '@ui/themes/use-light-theme-variables';
import { isAbsoluteUrl } from '@ui/utils/urls/is-absolute-url';
import { MenuItemConfig } from '@common/menus/menu-config';
import { useParams } from 'react-router-dom';
import { CloseIcon } from '@ui/icons/material/Close';

type NavbarColor = 'primary' | 'bg' | 'bg-alt' | 'transparent' | string;

export interface NavbarProps {
  hideLogo?: boolean | null;
  toggleButton?: ReactElement;
  children?: ReactNode;
  className?: string;
  color?: NavbarColor;
  bgOpacity?: number | string;
  darkModeColor?: NavbarColor;
  logoColor?: 'dark' | 'light';
  textColor?: string;
  primaryButtonColor?: ButtonColor;
  border?: string;
  size?: 'xs' | 'sm' | 'md';
  rightChildren?: ReactNode;
  menuPosition?: string;
  authMenuItems?: NavbarAuthUserProps['items'];
  alwaysDarkMode?: boolean;
  wrapInContainer?: boolean;
}
export function Navbar(props: NavbarProps) {
  let {
    hideLogo,
    toggleButton,
    children,
    className,
    border,
    size = 'md',
    color,
    textColor,
    darkModeColor,
    rightChildren,
    menuPosition,
    logoColor,
    primaryButtonColor,
    authMenuItems,
    alwaysDarkMode = false,
    wrapInContainer = false,
  } = props;
  const isDarkMode = useIsDarkMode() || alwaysDarkMode;
  const { notifications } = useSettings();
  const { isLoggedIn } = useAuth();
  const darkThemeVars = useDarkThemeVariables();
  const lightThemeVars = useLightThemeVariables();
  const showNotifButton = isLoggedIn && notifications?.integrated;
  const [mobileOpen, setMobileOpen] = useState(false);
  color = color ?? lightThemeVars?.['--be-navbar-color'] ?? 'primary';
  darkModeColor =
    darkModeColor ?? darkThemeVars?.['--be-navbar-color'] ?? 'bg-alt';

  if (isDarkMode) {
    color = darkModeColor;
  }
  const params = useParams();

  return (
    <>
    <div
      style={alwaysDarkMode ? darkThemeVars : undefined}
      className={clsx(
        getColorStyle(color, textColor),
        size === 'md' && '',
        size === 'sm' && '',
        size === 'xs' && '',
        border,
        className,
      )}
    >
      <div
      data-navbar 
        className={clsx(
          `
          ${!isLoggedIn && !params.hash && 'landing-container'
          } 
          flex text-white items-center justify-between gap-10 border-b px-8 py-4 md:py-6`,
          wrapInContainer && 'container mx-auto',
        )}
        style={{
          backgroundColor: isDarkMode
            ? '#111820' // Dark background
            : '#FFFFFF', // Light background
          color: isDarkMode ? '#FFFFFF' : '#000000', // Text color
        }}
      >
        {!hideLogo && (
          <Logo isDarkMode={isDarkMode} color={color} logoColor={logoColor} />
        )}
        {toggleButton}
        {children}
        <DesktopMenu position={menuPosition} />
        <div className="ml-auto flex items-center gap-4 md:gap-14">
          {rightChildren}
          {showNotifButton && <NotificationDialogTrigger />}
          {isLoggedIn ? (
            <NavbarAuthUser items={authMenuItems} />
          ) : (
            <NavbarAuthButtons
              navbarColor={color}
              primaryButtonColor={primaryButtonColor}
            />
          )}

          <MobileMenuButton
              open={mobileOpen}
              onToggle={() => setMobileOpen(p => !p)}
              position={menuPosition}
            />
        </div>
      </div>
    </div>
    <MobileMenuPanel
        position={menuPosition}
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        isLoggedIn={isLoggedIn}
      />
      </>
  );
}

interface DesktopMenuProps {
  position: NavbarProps['menuPosition'];
}
function DesktopMenu({ position }: DesktopMenuProps) {
  return (
    <CustomMenu
      className="mx-14 text-sm max-md:hidden"
      itemClassName={isActive =>
        clsx(
          'opacity-90 hover:underline hover:opacity-100',
          isActive && 'opacity-100',
        )
      }
      menu={position}
    />
  );
}

function MobileMenuButton({
  open,
  onToggle,
  position,
}: {
  open: boolean;
  onToggle: () => void;
  position?: string;
}) {
  const menu = useCustomMenu(position);
  if (!menu?.items.length) return null;

  return (
    <IconButton
      className="md:hidden"
      aria-label={open ? 'Close menu' : 'Open menu'}
      onClick={onToggle}
    >
      {open ? <CloseIcon /> : <MenuIcon />}
    </IconButton>
  );
}


function MobileMenuPanel({
  position,
  open,
  onClose,
  isLoggedIn,
}: {
  position?: string;
  open: boolean;
  onClose: () => void;
  isLoggedIn: boolean;
}) {
  const navigate = useNavigate();
  const menu = useCustomMenu(position);
  if (!menu?.items.length) return null;

  const go = (item: MenuItemConfig) => {
    onClose();
    if (isAbsoluteUrl(item.action)) {
      window.open(item.action, item.target)?.focus();
    } else {
      navigate(item.action);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9998,
          background: 'rgba(0,0,0,0.35)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.2s ease',
        }}
      />

      {/* Panel — exact same style as DropPanel in custom-menu.tsx */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '88vh',
          background: '#fff',
          boxShadow: '0 20px 45px rgba(0,0,0,0.12)',
          borderRadius: '0 0 16px 16px',
          opacity: open ? 1 : 0,
          transform: open ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.96)',
          transition: 'opacity 0.2s ease, transform 0.2s ease',
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 20px',
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 15, fontWeight: 500, color: 'rgba(0,0,0,0.72)' }}>
            Menu
          </span>
          <div
            onClick={onClose}
            style={{
              width: 30, height: 30,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 7,
              border: '1px solid rgba(0,0,0,0.12)',
              cursor: 'pointer',
              color: 'rgba(0,0,0,0.5)',
            }}
          >
            <CloseIcon size="sm" />
          </div>
        </div>

        {/* Menu list */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {menu.items.map(item => (
              <MenuItem key={item.id} item={item} depth={0} onNavigate={go} />
            ))}
          </ul>
        </nav>

        {/* Footer */}
        {!isLoggedIn && (
          <div
            style={{
              flexShrink: 0,
              display: 'flex',
              gap: 10,
              padding: '12px 16px',
              borderTop: '1px solid rgba(0,0,0,0.08)',
            }}
          >
            <button
              type="button"
              onClick={() => { onClose(); navigate('/login'); }}
              style={{
                flex: 1, padding: '9px 0',
                fontSize: 15, fontWeight: 450,
                color: 'rgba(0,0,0,0.72)',
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: 7,
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              <Trans message="Login" />
            </button>
            <button
              type="button"
              onClick={() => { onClose(); navigate('/register'); }}
              style={{
                flex: 1, padding: '9px 0',
                fontSize: 15, fontWeight: 450,
                color: '#fff',
                background: '#1a6bc4',
                border: 'none',
                borderRadius: 7,
                cursor: 'pointer',
              }}
            >
              <Trans message="Register" />
            </button>
          </div>
        )}
      </div>
    </>
  );
}


function MenuItem({
  item,
  depth,
  onNavigate,
}: {
  item: MenuItemConfig;
  depth: number;
  onNavigate: (item: MenuItemConfig) => void;
}) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const hasChildren = !!(item.children?.length);
  const IconCmp = item.icon && createSvgIconFromTree(item.icon);

  const isActive = hovered || (hasChildren && open);
  const pl = 16 + depth * 14;

  // ✅ Exact same as DropItem rowStyle in custom-menu.tsx
  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: `9px 8px 9px ${pl}px`,
    borderRadius: 7,
    fontSize: 15,
    fontWeight: 450,
    color: isActive ? '#1a6bc4' : 'rgba(0,0,0,0.72)',
    background: isActive ? '#EBF4FF' : 'transparent',
    cursor: 'pointer',
    transition: 'background 0.12s ease, color 0.12s ease',
    width: '100%',
    border: 'none',
    textAlign: 'left',
  };

  if (hasChildren) {
    return (
      <li style={{ listStyle: 'none' }}>
        <button
          type="button"
          style={rowStyle}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onTouchStart={() => setHovered(true)}
          onTouchEnd={() => setHovered(false)}
          onClick={() => setOpen(p => !p)}
        >
          {IconCmp && <IconCmp size="sm" style={{ opacity: 0.5, flexShrink: 0 }} />}
          <span style={{ flex: 1, textAlign: 'left' }}>
            <Trans message={item.label} />
          </span>
          {/* Same ChevDown as custom-menu.tsx */}
          <svg
            width="10" height="10" viewBox="0 0 10 10" fill="none"
            style={{
              flexShrink: 0, marginLeft: 3,
              transition: 'transform 0.18s ease, opacity 0.18s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              opacity: open ? 0.75 : 0.4,
            }}
          >
            <path d="M1.5 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div style={{
          overflow: 'hidden',
          maxHeight: open ? '999px' : '0px',
          transition: 'max-height 0.25s ease',
        }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: '0 0 0 8px' }}>
            {item.children!.map(child => (
              <MenuItem key={child.id} item={child} depth={depth + 1} onNavigate={onNavigate} />
            ))}
          </ul>
        </div>
      </li>
    );
  }

  return (
    <li style={{ listStyle: 'none' }}>
      <button
        type="button"
        style={rowStyle}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onTouchStart={() => setHovered(true)}
        onTouchEnd={() => setHovered(false)}
        onClick={() => onNavigate(item)}
      >
        {IconCmp && <IconCmp size="sm" style={{ opacity: 0.5, flexShrink: 0 }} />}
        <span style={{ flex: 1, textAlign: 'left' }}>
          <Trans message={item.label} />
        </span>
      </button>
    </li>
  );
}

function getColorStyle(color: string, textColor?: string): string {
  switch (color) {
    case 'primary':
      return `bg-primary-dark ${textColor || 'text-on-primary'
        } border-b-primary`;
    case 'bg':
      return `bg ${textColor || 'text-main'} border-b`;
    case 'bg-alt':
      return `bg-alt ${textColor || 'text-main'} border-b`;
    case 'transparent':
      return `bg-transparent ${textColor || 'text-white'}`;
    default:
      return `${color} ${textColor}`;
  }
}
